import React, { useEffect, useState } from 'react';
import { Brain, Shield, Building2, Cloud, Server, GitBranch, Workflow, Database, MessageSquare, ArrowRight, Play, Sparkles, Lock, Zap, CheckCircle, Code, Users, BarChart3 } from 'lucide-react';

const SelficEnterpriseHero = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [journeyStep, setJourneyStep] = useState(0);

    useEffect(() => {
        setIsVisible(true);
        const interval = setInterval(() => {
            setJourneyStep(prev => (prev + 1) % 5);
        }, 2500);
        return () => clearInterval(interval);
    }, []);

    const aiJourney = [
        {
            step: 1,
            title: "Connect Your Enterprise Systems",
            icon: <Database className="w-6 h-6" />,
            description: "Seamlessly integrate with your existing infrastructure",
            visual: "from-blue-500 to-cyan-500",
            position: "0%"
        },
        {
            step: 2,
            title: "Design & Train AI Agents",
            icon: <Brain className="w-6 h-6" />,
            description: "Build intelligent agents tailored to your needs",
            visual: "from-purple-500 to-pink-500",
            position: "25%"
        },
        {
            step: 3,
            title: "Embed in Chatbots & Apps",
            icon: <Workflow className="w-6 h-6" />,
            description: "Deploy AI across your digital touchpoints",
            visual: "from-green-500 to-emerald-500",
            position: "50%"
        },
        {
            step: 4,
            title: "Scale with Multi-Agent Automation",
            icon: <Shield className="w-6 h-6" />,
            description: "Orchestrate complex workflows with agent collaboration",
            visual: "from-orange-500 to-red-500",
            position: "75%"
        },
        {
            step: 5,
            title: "Monitor, Analyze & Optimize",
            icon: <BarChart3 className="w-6 h-6" />,
            description: "Gain insights and continuously improve performance",
            visual: "from-indigo-500 to-purple-500",
            position: "100%"
        }
    ];

    const platformCapabilities = [
        {
            icon: <Building2 className="w-7 h-7" />,
            title: "Multi-Agent Platform",
            description: "Create unlimited AI agents and chatbots from one platform"
        },
        {
            icon: <Server className="w-7 h-7" />,
            title: "On-Premises Deployment",
            description: "Full data sovereignty with secure installation"
        },
        {
            icon: <GitBranch className="w-7 h-7" />,
            title: "Hybrid & Cloud Options",
            description: "Flexible deployment for your infrastructure"
        },
        {
            icon: <Shield className="w-7 h-7" />,
            title: "Enterprise Security",
            description: "SSO, RBAC, and complete audit trails"
        }
    ];

    const deploymentOptions = [
        { icon: <Server className="w-5 h-5" />, name: "On-Premises", color: "text-blue-400" },
        { icon: <Cloud className="w-5 h-5" />, name: "Cloud", color: "text-purple-400" },
        { icon: <GitBranch className="w-5 h-5" />, name: "Hybrid", color: "text-green-400" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 text-white overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            </div>

            <div className="relative z-10">
                {/* Navigation */}
                <nav className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
                            <Brain className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                            Selfic AI
                        </span>
                    </div>
                    {/* <button className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2">
                        <span>Schedule Demo</span>
                        <ArrowRight className="w-4 h-4" />
                    </button> */}
                </nav>

                {/* Hero Section */}
                <div className="px-6 py-20 max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Column */}
                        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            <div className="inline-flex items-center space-x-2 bg-purple-500/20 px-4 py-2 rounded-full mb-6 border border-purple-500/30">
                                <Sparkles className="w-4 h-4 text-purple-400" />
                                <span className="text-sm text-purple-300">Enterprise AI Platform</span>
                            </div>

                            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                                Transform Your Enterprise with
                                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                                    {" "}Intelligent AI Agents
                                </span>
                            </h1>

                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                The complete platform to create, deploy, and manage AI agents. From consulting
                                to implementation, we help you automate operations with on-premises or cloud deployment.
                            </p>

                            {/* Key Benefits */}
                            <div className="space-y-3 mb-8">
                                <div className="flex items-center space-x-3 text-gray-300">
                                    <CheckCircle className="text-green-400 w-5 h-5" />
                                    <span>Complete AI transformation consulting</span>
                                </div>
                                <div className="flex items-center space-x-3 text-gray-300">
                                    <CheckCircle className="text-green-400 w-5 h-5" />
                                    <span>Seamless integration with existing systems</span>
                                </div>
                                <div className="flex items-center space-x-3 text-gray-300">
                                    <CheckCircle className="text-green-400 w-5 h-5" />
                                    <span>Full data sovereignty & security control</span>
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 mb-12">
                                <a
                                    href="mailto:contact@selficai.com?subject=Start Building with SelficAI"
                                    className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-4 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 text-lg font-semibold group hover:shadow-2xl"
                                >
                                    <span>Schedule Demo</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </a> 
                            </div>

                            {/* Deployment Options */}
                            <div className="flex items-center space-x-4 pt-6 border-t border-white/10">
                                <span className="text-sm text-gray-400">Deploy your way:</span>
                                {deploymentOptions.map((option, index) => (
                                    <div key={index} className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
                                        <div className={option.color}>{option.icon}</div>
                                        <span className="text-sm">{option.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Column - AI Journey Animation */}
                        <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                            <div className="relative">
                                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
                                    <h3 className="text-2xl font-bold mb-8">Your AI Journey</h3>

                                    {/* Animated Progress Bar */}
                                    <div className="relative h-2 bg-white/10 rounded-full mb-8 overflow-hidden">
                                        <div
                                            className="absolute h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-500"
                                            style={{ width: aiJourney[journeyStep].position }}
                                        ></div>
                                    </div>

                                    {/* Current Step Display */}
                                    <div className="mb-6">
                                        <div className="flex items-start space-x-4">
                                            <div className={`w-16 h-16 bg-gradient-to-br ${aiJourney[journeyStep].visual} rounded-2xl flex items-center justify-center flex-shrink-0 animate-pulse`}>
                                                {aiJourney[journeyStep].icon}
                                            </div>
                                            <div>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <span className="text-xs font-semibold text-purple-400 uppercase tracking-wide">
                                                        Step {aiJourney[journeyStep].step}
                                                    </span>
                                                </div>
                                                <h4 className="text-xl font-bold mb-2">{aiJourney[journeyStep].title}</h4>
                                                <p className="text-gray-400 text-sm">{aiJourney[journeyStep].description}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* All Steps */}
                                    <div className="space-y-2">
                                        {aiJourney.map((step, index) => (
                                            <div
                                                key={index}
                                                className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${index === journeyStep ? 'bg-white/10 scale-105' : 'bg-white/5'
                                                    }`}
                                            >
                                                <div className={`w-10 h-10 bg-gradient-to-br ${step.visual} rounded-lg flex items-center justify-center flex-shrink-0 ${index === journeyStep ? '' : 'opacity-50'
                                                    }`}>
                                                    {React.cloneElement(step.icon, { className: 'w-5 h-5' })}
                                                </div>
                                                <div className="flex-1">
                                                    <p className={`text-sm font-medium ${index === journeyStep ? 'text-white' : 'text-gray-400'}`}>
                                                        {step.title}
                                                    </p>
                                                </div>
                                                {index <= journeyStep && (
                                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Floating Badge */}
                                <div className="absolute -top-6 -right-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-4 shadow-2xl animate-bounce">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold">5</p>
                                        <p className="text-xs">Steps</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Platform Capabilities */}
                    <div className={`mt-32 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold mb-4">One Platform. Complete Control.</h2>
                            <p className="text-gray-400 max-w-3xl mx-auto text-lg">
                                Build, deploy, and manage your entire AI ecosystem with enterprise-grade capabilities
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {platformCapabilities.map((capability, index) => (
                                <div
                                    key={index}
                                    className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-400/50 transition-all duration-300 group hover:scale-105"
                                >
                                    <div className="text-purple-400 mb-4 group-hover:scale-110 transition-transform">
                                        {capability.icon}
                                    </div>
                                    <h3 className="text-lg font-semibold mb-2">{capability.title}</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">{capability.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Use Cases */}
                    <div className={`mt-24 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/20">
                            <div className="text-center mb-12">
                                <h2 className="text-3xl font-bold mb-4">AI Solutions Across Your Enterprise</h2>
                                <p className="text-gray-400 max-w-2xl mx-auto">
                                    Transform every department with intelligent automation
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { icon: <MessageSquare className="w-6 h-6" />, title: "Customer Service", desc: "24/7 Support & Engagement" },
                                    { icon: <BarChart3 className="w-6 h-6" />, title: "Operations", desc: "Workflow Automation" },
                                    { icon: <Users className="w-6 h-6" />, title: "Sales & Marketing", desc: "Lead Intelligence" },
                                    { icon: <Code className="w-6 h-6" />, title: "IT & Internal", desc: "Helpdesk & Knowledge" }
                                ].map((useCase, index) => (
                                    <div
                                        key={index}
                                        className="bg-white/5 p-6 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                                    >
                                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                            {useCase.icon}
                                        </div>
                                        <h3 className="font-semibold mb-2">{useCase.title}</h3>
                                        <p className="text-sm text-gray-400">{useCase.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Final CTA */}
                    <div className={`mt-24 transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                            <div className="relative z-10">
                                <h2 className="text-4xl font-bold mb-4">We Don't Just Sell Software</h2>
                                <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">
                                    We partner with you through your complete AI transformation journey, from strategy to implementation and beyond — all from one unified platform
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center"> 
                                    <a
                                        href="mailto:contact@selficai.com?subject=Contact Sales"
                                        className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-purple-600 transition-all duration-300"
                                    >
                                        Contact Sales
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
            </div> 
        </div>
    );
};

export default SelficEnterpriseHero;